import { useCallback, useEffect, useState } from 'react';
import api from '../../lib/api';
import { Check, Copy, ExternalLink } from 'lucide-react';

type PlunkWebhookGuideResponse = {
  backendPublicUrlConfigured: boolean;
  webhookUrl: string;
  webhookPath: string;
  httpMethod: string;
  webhookSecretConfigured: boolean;
  plunkAuthorizationHeaderValue: string | null;
  platformEmail: {
    fromAddress: string;
    fromName: string | null;
    nodemailerFromLine: string;
    addressResolvedFrom: 'EMAIL_FROM_ADDRESS' | 'SMTP_FROM' | 'SMTP_USER' | 'default';
    nameConfigured: boolean;
  };
  authorizationHeader: {
    name: string;
    valueTemplate: string;
    instructions: string;
  };
  plunkDocsUrl: string;
  emailDeliveryLogApi: string | null;
  note: string;
};

function CopyField({ label, value, hint }: { label: string; value: string; hint?: string }) {
  const [copied, setCopied] = useState(false);

  const copy = useCallback(async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  }, [value]);

  return (
    <div className="rounded-xl border border-glass bg-glass-1/80 p-4">
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="text-xs font-medium uppercase tracking-wide text-glass-text-2">{label}</span>
        <button
          type="button"
          onClick={copy}
          disabled={!value}
          className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-medium text-accent2 hover:bg-glass-3 disabled:opacity-40"
        >
          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <code className="block break-all text-sm text-glass-text font-mono">{value || '—'}</code>
      {hint ? <p className="mt-2 text-xs text-glass-text-2">{hint}</p> : null}
    </div>
  );
}

export default function AdminPlunkWebhookGuide() {
  const [data, setData] = useState<PlunkWebhookGuideResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const { data: res } = await api.get<PlunkWebhookGuideResponse>('/admin/plunk-webhook-guide');
        if (active) setData(res);
      } catch (e: unknown) {
        const msg =
          e && typeof e === 'object' && 'response' in e
            ? (e as { response?: { data?: { error?: string } } }).response?.data?.error
            : null;
        if (active) setError(msg || 'Failed to load Plunk email settings');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="p-6 md:p-8 max-w-3xl">
        <p className="text-glass-text-2">Loading…</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-6 md:p-8 max-w-3xl">
        <p className="text-red-400">{error || 'Unknown error'}</p>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 max-w-3xl space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-glass-text tracking-tight">Plunk Email</h1>
        <p className="mt-2 text-sm text-glass-text-2 leading-relaxed">
          Platform email sender (env), Plunk transactional API, and delivery webhooks. Values below are read from your
          server environment — set them in Railway (or <code className="text-xs">.env</code>) and redeploy; no extra
          database table.
        </p>
      </div>

      <section className="rounded-xl border border-glass bg-glass-1/50 p-5 space-y-4">
        <h2 className="text-lg font-semibold text-glass-text">Platform sender (From)</h2>
        <p className="text-xs text-glass-text-2 leading-relaxed">
          Matches the idea of Supabase Auth → SMTP → sender fields. Backend uses{' '}
          <code className="text-glass-text">EMAIL_FROM_ADDRESS</code> and optional{' '}
          <code className="text-glass-text">EMAIL_FROM_NAME</code> for{' '}
          <code className="text-glass-text">sendMail</code> and for Plunk <code className="text-glass-text">/v1/send</code>{' '}
          when no custom <code className="text-glass-text">from</code> is passed. If <code className="text-glass-text">EMAIL_FROM_ADDRESS</code>{' '}
          is empty, the address falls back to <code className="text-glass-text">SMTP_FROM</code> then{' '}
          <code className="text-glass-text">SMTP_USER</code>.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-lg border border-glass bg-glass-1/80 p-3">
            <span className="text-xs font-medium uppercase tracking-wide text-glass-text-2">From address (resolved)</span>
            <code className="mt-1 block break-all text-sm font-mono text-glass-text">{data.platformEmail.fromAddress}</code>
            <p className="mt-2 text-[0.65rem] text-glass-text-3">
              Source: <code className="text-glass-text">{data.platformEmail.addressResolvedFrom}</code>
            </p>
          </div>
          <div className="rounded-lg border border-glass bg-glass-1/80 p-3">
            <span className="text-xs font-medium uppercase tracking-wide text-glass-text-2">Display name</span>
            <code className="mt-1 block break-all text-sm font-mono text-glass-text">
              {data.platformEmail.fromName || '—'}
            </code>
            <p className="mt-2 text-[0.65rem] text-glass-text-3">
              {data.platformEmail.nameConfigured
                ? 'From EMAIL_FROM_NAME'
                : 'Set EMAIL_FROM_NAME in env to show a friendly name'}
            </p>
          </div>
        </div>
        <CopyField
          label="SMTP From line (Nodemailer preview)"
          value={data.platformEmail.nodemailerFromLine}
          hint="What the backend uses as the From header for app SMTP sends."
        />
      </section>

      {!data.backendPublicUrlConfigured && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-glass-text">
          Set <code className="text-xs">BACKEND_PUBLIC_URL</code> on your API host (e.g. Railway) to your public API
          origin, without a trailing slash — for example <code className="text-xs">https://api.yourdomain.com</code>.
          Then reload this page to copy the full webhook URL.
        </div>
      )}

      {!data.webhookSecretConfigured && (
        <div className="rounded-xl border border-glass bg-glass-2/50 px-4 py-3 text-sm text-glass-text-2">
          <code className="text-xs text-glass-text">PLUNK_WEBHOOK_SECRET</code> is not set on the server. The webhook
          endpoint will accept unsigned requests. For production, set a long random secret in your host env and add the
          Authorization header in Plunk as described below.
        </div>
      )}

      <section className="space-y-3">
        <h2 className="text-lg font-semibold text-glass-text">Delivery webhooks (Plunk Workflow step)</h2>
        <p className="text-sm text-glass-text-2">
          Plunk sends events through <strong className="text-glass-text">Workflows</strong>. Each trigger (e.g.{' '}
          <code className="text-xs">email.delivery</code>) is its own workflow; use the <strong className="text-glass-text">same URL</strong> for
          each.
        </p>

        <div>
          <p className="text-sm text-glass-text-2 mb-2">
            <strong className="text-glass-text">Step name</strong> — any label you like (e.g. “Notify myKOL API —
            delivery”).
          </p>
        </div>

        <CopyField
          label="Webhook URL"
          value={data.webhookUrl}
          hint="Paste into Plunk → Workflows → your workflow → Webhook → URL."
        />

        <CopyField
          label="HTTP method"
          value={data.httpMethod}
          hint="Plunk defaults to POST; keep POST."
        />

        <div className="rounded-xl border border-glass bg-glass-1/80 p-4 space-y-3">
          <h3 className="text-sm font-semibold text-glass-text">HTTP headers (optional but recommended)</h3>
          <p className="text-xs text-glass-text-2 leading-relaxed">{data.authorizationHeader.instructions}</p>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <span className="text-xs font-medium uppercase tracking-wide text-glass-text-2">Header name</span>
              <code className="mt-1 block text-sm font-mono text-glass-text">{data.authorizationHeader.name}</code>
            </div>
            {data.plunkAuthorizationHeaderValue ? (
              <CopyField
                label="Header value (copy into Plunk)"
                value={data.plunkAuthorizationHeaderValue}
                hint="Paste this entire line as the header value in Plunk → Webhook → Headers. Treat it like a password."
              />
            ) : (
              <div className="rounded-lg border border-glass bg-glass-2/50 p-3 text-xs text-glass-text-2">
                Set <code className="text-glass-text">PLUNK_WEBHOOK_SECRET</code> on the API host, redeploy, and reload
                this page to copy the full <code className="text-glass-text">Bearer …</code> value.
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="rounded-xl border border-glass bg-glass-1/50 p-5 space-y-3">
        <h2 className="text-lg font-semibold text-glass-text">Dashboard checklist</h2>
        <ol className="list-decimal list-inside space-y-2 text-sm text-glass-text-2 leading-relaxed">
          <li>Open Plunk → Workflows → Create workflow.</li>
          <li>
            Choose trigger <code className="text-xs text-glass-text">email.delivery</code> (or bounce, open, etc.).
          </li>
          <li>
            Add a <strong className="text-glass-text">Webhook</strong> step; paste the URL and method from above.
          </li>
          <li>Add the Authorization header if you configured PLUNK_WEBHOOK_SECRET.</li>
          <li>Enable the workflow.</li>
          <li>
            Repeat for other events you care about — use the <strong className="text-glass-text">same webhook URL</strong>{' '}
            each time.
          </li>
        </ol>
        <p className="text-xs text-glass-text-2 border-t border-glass pt-3">{data.note}</p>
        <a
          href={data.plunkDocsUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-accent2 hover:underline"
        >
          Plunk webhooks documentation
          <ExternalLink className="w-4 h-4" />
        </a>
      </section>

      <section className="text-xs text-glass-text-2 space-y-1">
        <p>
          <strong className="text-glass-text">Audit API</strong> —{' '}
          <code className="text-glass-text">GET /api/admin/email-deliveries</code>
          {data.emailDeliveryLogApi ? (
            <>
              {' '}
              — full URL: <code className="text-glass-text break-all">{data.emailDeliveryLogApi}</code>
            </>
          ) : (
            <> (set BACKEND_PUBLIC_URL to show the full URL here).</>
          )}
        </p>
      </section>
    </div>
  );
}
